/* PR 45 review · D2 (Card V3) — an older engagement with no country, no currency or no saved weekend shows a banner at the
   top of the page content (EN + AR) linking to its Engagement Settings; dismissed per browser session (per engagement). New
   engagements always have them (the server refuses to create one without them).
   PR 45 round 2 · R9: the banner also asks to confirm the currency of an older engagement — "to confirm" until a team member
   saves it once in Engagement Settings (``currency_confirmed_at``; set at create for new engagements). */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { AlertTriangle, X } from "lucide-react";

const KEY = (id) => `azex_locale_banner_dismissed_${id}`;

export function localeMissing(eng) {
  if (!eng?.id) return [];
  const out = [];
  if (!String(eng.country || "").trim()) out.push("bannerCountry");
  if (!String(eng.currency ?? "").trim()) out.push("bannerCurrency");
  if (!Array.isArray(eng.non_working_days?.weekdays)) out.push("bannerWeekend");
  return out;
}

/** R9: a stored currency nobody has confirmed since the D2 release. */
export const currencyToConfirm = (eng) => !!eng?.id && !!String(eng.currency ?? "").trim() && !eng.currency_confirmed_at;

export default function EngagementLocaleBanner() {
  const { t } = useI18n();
  const { current } = useEngagement();
  const missing = localeMissing(current);
  const confirm = currencyToConfirm(current);
  // Batch 32: a signed-off (locked, read-only) engagement cannot save its settings — no banner asking to
  const show = !current?.signed_off && (missing.length > 0 || confirm);
  const [hidden, setHidden] = useState(true);
  useEffect(() => {
    if (!current?.id || !show) { setHidden(true); return; }
    try { setHidden(sessionStorage.getItem(KEY(current.id)) === "1"); } catch { setHidden(false); }
  }, [current?.id, show]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!current?.id || !show || hidden) return null;
  const dismiss = () => { try { sessionStorage.setItem(KEY(current.id), "1"); } catch { /* private window */ } setHidden(true); };
  const what = missing.map((k) => t(`engLocale.${k}`)).join(t("engLocale.bannerAnd"));
  return (
    <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
         data-testid="eng-locale-banner">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {missing.length > 0 && (
          <span className="font-medium" data-testid="eng-locale-banner-missing">
            {t("engLocale.bannerMissing").replace("{what}", what)}
            {missing.includes("bannerWeekend") ? ` ${t("engLocale.bannerWeekendRule")}` : ""}
          </span>
        )}
        {confirm && (
          <span className="font-medium" data-testid="eng-locale-banner-confirm">
            {t("engLocale.bannerCurrencyConfirm").replace("{cur}", String(current.currency).trim())}
          </span>
        )}
        <Link to={`/engagements?settings=${encodeURIComponent(current.id)}`} className="underline" data-testid="eng-locale-banner-link">
          {t("engLocale.bannerLink")}
        </Link>
        <button type="button" className="ms-auto rounded p-0.5 hover:bg-amber-100" onClick={dismiss} aria-label="×" data-testid="eng-locale-banner-dismiss">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
